import React, { JSX } from "react";

import TutorialsService from "@app/tutorials/tutorialsService";
import { TutorialContent, loadingData, errorData } from "@app/tutorials/tutorialsShared"
import { EditTutorialsFlyout } from "./EditTutorialsFlyout";
import { EditTutorialsHyperlinkFlyout } from "./EditTutorialsHyperlinkFlyout"
import { ModalLink } from "@app/shared/Modals";
import { toast } from "react-toastify";
import { AddTutorialFlyout } from "./AddTutorialFlyout";
import {AddTutorialHyperlinkFlyout } from "./AddTutorialHyperlinkFlyout"
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";

interface TutorialListProps {
  containerName: string;
  friendlyName?: string;
  editOnly?: boolean;
  enableHyperlink?: boolean;
}

interface TutorialListState {
  draggedFieldId: number;
  dropFieldId: number;
  tutorials: TutorialContent[];
}

export default class EditTutorialsList extends React.Component<TutorialListProps, TutorialListState> {
  constructor(props: TutorialListProps) {
    super(props);

    this.state = {
      draggedFieldId: null,
      dropFieldId: null,
      tutorials: loadingData
    }
  }

  componentDidMount() {
    this.loadContent();
  }

  private loadContent = () => {
    this.supportService.showBlobSas(this.props.containerName)
      .then((tutorialsStr) => {
        this.setState({ tutorials: JSON.parse(tutorialsStr) });
      })
      .catch(e => {
        console.error(e);
        this.setState({ tutorials: errorData });
      });
  }

  private readonly supportService = new TutorialsService();

  public render() {
    const tutorials = this.state.tutorials;
    const buildRows = tutorials.map((row, index) => this.renderLines(row, index));

    return (
      <div className="grid-striped">
        {buildRows}
        {this.dragDropPadding(tutorials.length)}
        {this.newFileButton()}
        {this.newHyperlinkButton()}
      </div>
    );
  }

  private dragDropPadding(listLen: number) {
    const expanded = this.isDragging();
    const insertable = this.state.dropFieldId == listLen;
    return (
      <div
        style={
          {
            borderTop: insertable && expanded ? '2px dotted gray' : 'none',
            height: expanded ? '40px' : '0px',
            transition: '0.1s height ease-out'
          }
        }
        onDragEnter={() => { this.dragEnterHandler(listLen); }}
      />
    );
  }

  private newFileButton() {
    return (
      !(this.props.editOnly ?? false) && (
        <FlyoutLink
          content={{
            body: <AddTutorialFlyout
              container={this.props.containerName}
              fileExtensionSplitter={fileExtensionSplitter}
              onUpload={(displayTitle: string, newDataStr: string) => {
                toast(<span>Tutorial &quot;{displayTitle}&quot; successfully added</span>, {
                  containerId: "z-index-9999",
                  type: "success",
                });
                this.updateContainerUI(newDataStr);
              }}
            />,
            title: "Add Tutorial File",
          }}
          classNames="qes-button-secondary"
          testId="edit-tutorials__add-new-flyout"
          size="40"
        >
          <i className="fa fa-plus pr5" /> Add New File to {this.props.friendlyName ?? this.props.containerName}
        </FlyoutLink>
      )
    );
  }

  private newHyperlinkButton() {
    return (
      !(this.props.editOnly ?? false) && (this.props.enableHyperlink ?? false) && (
        <div>
          <FlyoutLink
            content={{
              body: <AddTutorialHyperlinkFlyout
                container={this.props.containerName}
                fileExtensionSplitter={fileExtensionSplitter}
                onSubmit={(displayTitle: string, newDataStr: string) => {
                  toast(<span>Tutorial &quot;{displayTitle}&quot; successfully added</span>, {
                    containerId: "z-index-9999",
                    type: "success",
                  });
                  this.updateContainerUI(newDataStr);
                }}
              />,
              title: "Add Tutorial Hyperlink",
            }}
            classNames="qes-button-secondary"
            testId="edit-tutorials__add-new-flyout"
            size="40"
          >
            <i className="fa fa-plus pr5" /> Add Hyperlink to {this.props.friendlyName ?? this.props.containerName}
            </FlyoutLink>
        </div>
      )
    );
  }

  private isDragging = () => this.state.draggedFieldId != null;

  private dragStartHandler(index: number) {
    this.setState({
      draggedFieldId: index,
      dropFieldId: index
    });
  }

  private dragEndHandler(index: number) {
    if (this.isDragging()) {
      const newIndex = this.state.dropFieldId;
      const reordered = this.state.tutorials;
      const editHyperlink = "null";

      const draggedField = reordered[index];

      if (newIndex != index) {
        const formData = new FormData();
        formData.set("index", newIndex.toString());

        if (draggedField.filetype == "txt") {
          this.supportService.updateHyperlinkBlobSas(this.props.containerName, draggedField.title, editHyperlink, newIndex)
            .then((response) => { this.updateContainerUI(response) });
        }
        else {
          this.supportService.updateBlobSas(this.props.containerName, draggedField.filename, draggedField.filetype, draggedField.title, formData)
            .then((response) => { this.updateContainerUI(response) });
        }
      }

      // indexes shift as we do this, so this ensures correctness
      if (index > newIndex) {
        reordered.splice(index, 1);
        reordered.splice(newIndex, 0, draggedField);
      } else {
        reordered.splice(newIndex, 0, draggedField);
        reordered.splice(index, 1);
      }

      this.setState({ draggedFieldId: null, dropFieldId: null, tutorials: reordered });
    }
  }

  private dragEnterHandler(index: number) {
    if (this.isDragging()) {
      this.setState({ dropFieldId: index });
    }
  }

  private renderLines(row: TutorialContent, index: number) {
    let linkImageElement: JSX.Element;
    let flyoutLinkBody: JSX.Element;
    switch (row.filetype) {
      case "pdf":
        linkImageElement = (
          <i className="fa fa-file-pdf-o text-danger" />
        );
        flyoutLinkBody = (<EditTutorialsFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          fileExtensionSplitter={fileExtensionSplitter}
          rowTitle={row.title}
          updateContainerUI={this.updateContainerUI}
        />);
        break;
      case "mp4":
        linkImageElement = (
          <i className="fa fa-play text-info" />
        );
        flyoutLinkBody = (<EditTutorialsFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          fileExtensionSplitter={fileExtensionSplitter}
          rowTitle={row.title}
          updateContainerUI={this.updateContainerUI}
        />);
        break;
      case "txt":
        linkImageElement = (
          <i className="fa fa-link text-info" />
        );
        flyoutLinkBody = (<EditTutorialsHyperlinkFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          rowTitle={row.title}
          updateContainerUI={this.updateContainerUI}
          hyperlinkURL={row.link}
        />);
        break;
      default:
        linkImageElement = (
          <i className="fa" />
        );
        flyoutLinkBody = (<EditTutorialsFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          fileExtensionSplitter={fileExtensionSplitter}
          rowTitle={row.title}
          updateContainerUI={this.updateContainerUI}
        />);
    }

    const insertIndicator = index === this.state.dropFieldId
      ? { borderTop: '2px dotted gray' }
      : {};

    return (
      <div
        className={"d-flex flex-row align-items-center w-100 pb10 pt10 grid-row"}
        style={insertIndicator}
        key={index}
        onDragEnter={() => this.dragEnterHandler(index)}
        onDragEnd={() => this.dragEndHandler(index)}
      >

        <p className="ml10 mb0 mr10">
          <span
            className="drag-indicator"
            onDragStart={() => this.dragStartHandler(index)}
            draggable
          />
          <a className="text-decoration-none" style={{ color: "inherit" }} href={row.link} target="_blank" rel="noreferrer">
            {linkImageElement} <span className="mb0 mt0 ml10">{row.title}</span>
          </a>
        </p>
        <span className="btn-group ml-auto">
          <span className="ml5 mr10">
            <FlyoutLink
              content={{
                body: flyoutLinkBody,
                title: `Edit ${row.title}`,
              }}
              classNames="qes-button-secondary"
              testId="edit-tutorials__edit-flyout"
              size="40"
            >
              <i className="fa fa-edit" /> Edit
            </FlyoutLink>
          </span>
          <span className="d-inline-block ml-3">
            <ModalLink
              classNames="qes-button-secondary danger"
              content={{
                body: "Are you sure you want to delete this tutorial?",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => this.deleteRow(row.path, row.filename, row.filetype, row.title),
                confirmButtonText: "Delete",
                confirmButtonType: "primary-danger",
                title: `Delete ${row.title}`,
              }}
              testId="edit-tutorials-list__delete-modal"
              hideBodyScroll={true}
            >
              Delete
            </ModalLink>
          </span>
        </span>
      </div>
    );
  }

  private async deleteRow(containerReference: string, containerItem: string, itemExtension: string, displayTitle: string) {
    const newDataStr = await this.supportService.deleteBlobSas(containerReference, containerItem, itemExtension);
    this.updateContainerUI(newDataStr);
    toast(<span>Tutorial &quot;{displayTitle}&quot; successfully deleted</span>, {
      containerId: "z-index-9999",
      type: "success",
    });
  }

  private updateContainerUI = (newDataStr: string) => {
    const newData: TutorialContent[] = JSON.parse(newDataStr);
    this.setState({ tutorials: newData });
  }

}

function fileExtensionSplitter(filename: string): [string, string] {
  const splitname = filename.split('.');
  const extension = (splitname.length > 1) ? splitname.pop() : "";
  const basename = splitname.join("");
  return [basename, extension];
}



export { EditTutorialsList };
